import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { FiChevronLeft, FiChevronRight, FiX } from "react-icons/fi";
import { COLORS } from "../utils/constants";

const Overlay = styled.div`
  align-items: center;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  inset: 0;
  justify-content: center;
  position: fixed;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${COLORS.white};
  border-radius: 12px;
  max-height: 90vh;
  max-width: 90vw;
  overflow: hidden;
  position: relative;
`;

const CloseButton = styled.button`
  align-items: center;
  background: ${COLORS.gray800};
  border: none;
  border-radius: 50%;
  color: ${COLORS.white};
  cursor: pointer;
  display: flex;
  height: 36px;
  justify-content: center;
  position: absolute;
  right: 12px;
  top: 12px;
  width: 36px;
  z-index: 1;

  &:hover {
    background: ${COLORS.gray700};
  }
`;

const NavButton = styled.button`
  align-items: center;
  background: ${COLORS.gray800};
  border: none;
  border-radius: 50%;
  color: ${COLORS.white};
  cursor: pointer;
  display: flex;
  height: 36px;
  justify-content: center;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 36px;
  z-index: 1;

  &:hover {
    background: ${COLORS.gray700};
  }
`;

const PrevButton = styled(NavButton)`
  left: 12px;
`;

const NextButton = styled(NavButton)`
  right: 12px;
`;

const Counter = styled.div`
  background: ${COLORS.gray800};
  border-radius: 12px;
  bottom: 12px;
  color: ${COLORS.white};
  font-size: 12px;
  left: 50%;
  padding: 4px 12px;
  position: absolute;
  transform: translateX(-50%);
  z-index: 1;
`;

const ReceiptImage = styled.img`
  display: block;
  max-height: 85vh;
  max-width: 85vw;
  object-fit: contain;
`;

const ReceiptPdf = styled.iframe`
  border: none;
  display: block;
  height: 85vh;
  width: 85vw;
`;

const ReceiptModal = (props) => {
  const receipts = props.receipts || [];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [receipts.length]);

  const handlePrev = useCallback(() => {
    setIndex((i) => (i > 0 ? i - 1 : receipts.length - 1));
  }, [receipts.length]);

  const handleNext = useCallback(() => {
    setIndex((i) => (i < receipts.length - 1 ? i + 1 : 0));
  }, [receipts.length]);

  useEffect(() => {
    if (receipts.length <= 1) return;
    const handleKey = (e) => {
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [receipts.length, handlePrev, handleNext]);

  if (receipts.length === 0) return null;

  const current = receipts[index] || receipts[0];

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      props.onClose();
    }
  };

  return (
    <Overlay onClick={handleOverlayClick}>
      <ModalContent>
        <CloseButton onClick={props.onClose}>
          <FiX size={20} />
        </CloseButton>
        {receipts.length > 1 && (
          <React.Fragment>
            <PrevButton onClick={handlePrev}>
              <FiChevronLeft size={20} />
            </PrevButton>
            <NextButton onClick={handleNext}>
              <FiChevronRight size={20} />
            </NextButton>
            <Counter>
              {index + 1} / {receipts.length}
            </Counter>
          </React.Fragment>
        )}
        {current.endsWith(".pdf") ? (
          <ReceiptPdf src={current} title="Receipt PDF" />
        ) : (
          <ReceiptImage src={current} alt="Receipt" />
        )}
      </ModalContent>
    </Overlay>
  );
};

export default ReceiptModal;
